import { useId } from 'react';
import { getKeyHandler } from 't-lib-ui';
import './DiscreteSlider.css';

export interface SliderStop {
  value: number;
  label: string;
}

interface DiscreteSliderProps {
  label: string;
  /** Ordered stops; by convention the first is { value: 0, label: 'Off' }. */
  stops: SliderStop[];
  /** Current value; must be one of the stop values (0 = off). */
  value: number;
  onChange: (value: number) => void;
}

/**
 * A slider with a fixed set of labeled stops. The native range input maps to stop
 * indices, so ArrowLeft/Right move exactly one stop (FlowForm sideways navigation is
 * disabled while it has focus; up/down still move between form lines).
 */
export default function DiscreteSlider({ label, stops, value, onChange }: DiscreteSliderProps) {
  const id = useId();
  const index = Math.max(0, stops.findIndex(s => s.value == value));

  return (
    <div className="discreteSlider">
      <label className="discreteSliderLabel" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="range"
        className="discreteSliderInput flowFocussable"
        min={0}
        max={stops.length - 1}
        step={1}
        value={index}
        onChange={ev => onChange(stops[parseInt(ev.target.value, 10)].value)}
        onKeyDown={getKeyHandler({
          enterBehaviour: 'next',
          // Move one stop ourselves: always consume left/right so neither FlowForm
          // navigation nor global key handlers (e.g. post navigation) react to them.
          overrideHandlers: {
            ArrowLeft: () => { if (index > 0) onChange(stops[index - 1].value); return true; },
            ArrowRight: () => { if (index < stops.length - 1) onChange(stops[index + 1].value); return true; },
          },
        })}
        aria-valuetext={stops[index].label}
      />
      <div className="discreteSliderTicks">
        {stops.map((stop, i) => (
          <span
            key={stop.value}
            className={`discreteSliderTick${i == index ? ' active' : ''}`}
            onClick={() => onChange(stop.value)}
          >
            {stop.label}
          </span>
        ))}
      </div>
    </div>
  );
}
